import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { Event } from '../../types';

import { CARD_WIDTH_FULL_WIDTH } from 'constants/widths';

import { Box, Typography } from '@mui/material';

import { DayPart, PlanningEntityType } from 'generated/index';
import { panelThemeColors, PanelTheme } from '../ActivityPanel/constants';
import {
  ActivityCard,
  ConditionalDraggableCard
} from '../ActivityCard/ActivityCard';

const List = styled(Box)<{ height?: string }>`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH_FULL_WIDTH};
  height: ${({ height }) => height || '100%'};
  list-style: none;
` as typeof Box;

const Container = styled(Box)`
  margin-top: 8px;
`;

interface SortedToPlanListProps {
  activeEntityContainingSelection: string | null;
  dayPart?: DayPart;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  readOnly?: boolean;
  events: Event[];
  intervalPercentile?: number;
  timelineRouteWidth?: number;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  toPlanEntityId?: string;
  multiplePlanningEntitiesSelected?: boolean;
}

const SortedToPlanList = memo(
  ({
    activeEntityContainingSelection,
    dayPart = 'MORNING',
    planningEntityType,
    planningEntityId = '',
    events,
    onChangeMousePosition,
    onChangeActiveEntity,
    onChangeSelection,
    readOnly,
    multiplePlanningEntitiesSelected
  }: SortedToPlanListProps) => {
    const categorizeEvents = () => {
      const dateCategories: { [key: string]: Event[] } = {};
      events.forEach((event: Event) => {
        dateCategories[event.date] = [
          ...(dateCategories[event.date] || []),
          event
        ];
      });
      return dateCategories;
    };
    const sortedEvents: { [key: string]: Event[] } = useMemo(
      () => categorizeEvents(),
      [events]
    );

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const isActiveEntity: boolean =
      activeEntityContainingSelection === planningEntityId;

    const { backgroundColor } = panelThemeColors[dayPart as PanelTheme];

    const onHandleSelect = useCallback((id: string) => {
      setSelectedIds((selectedIds) => {
        // Call onChangeActiveEntity from parent component so it may update activeEntityContainingSelection
        onChangeActiveEntity?.(planningEntityId);

        if (selectedIds.includes(id))
          return selectedIds.filter((value) => value !== id);
        if (!selectedIds.length) return [id];
        return selectedIds.concat(id);
      });
    }, []);

    // If the active entity (the route) is *not* the entity containing the selected appointments, clear the selection
    useEffect(() => {
      if (!isActiveEntity && selectedIds.length > 0) setSelectedIds([]);
    }, [isActiveEntity]);

    // If the selection changes, notify the parent
    useEffect(() => {
      onChangeSelection?.(selectedIds);
    }, [selectedIds]);

    return (
      <List component="ul" width={1} gap={1}>
        {Object.entries(sortedEvents)
          .sort(([a], [b]) => (dayjs(a).isBefore(b) ? -1 : 1))
          .map(([dateLabel, events], i: number) => {
            const planningEntityName = events[0]?.organizationalUnit?.name;

            return (
              <Box key={i}>
                <Box
                  display="flex"
                  justifyContent="center"
                  mt={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    width: '100%'
                  }}
                >
                  {multiplePlanningEntitiesSelected && (
                    <Typography
                      variant="body2"
                      sx={{
                        height: '20px',
                        padding: '2px 8px',
                        boxSizing: 'border-box',
                        borderRadius: '10px',
                        backgroundColor,
                        textAlign: 'center',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        maxWidth: '190px',
                        marginRight: '8px'
                      }}
                    >
                      {planningEntityName}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      height: '20px',
                      padding: '2px 8px',
                      boxSizing: 'border-box',
                      borderRadius: '10px',
                      backgroundColor,
                      textAlign: 'center'
                    }}
                  >
                    {dayjs(new Date(dateLabel))
                      .startOf('day')
                      .format('DD-MM-YYYY')}
                  </Typography>
                </Box>

                <Box m="4px 0">
                  {events.map((event: Event, j: number) => {
                    const { isAbsent, start, id } = event;

                    if (readOnly || isAbsent) {
                      return (
                        <Container key={`${j}-${start}-${id}`}>
                          <ActivityCard
                            selected={false}
                            planningEntityId={planningEntityId}
                            planningEntityType={planningEntityType}
                            isTaskList
                            hovered={true}
                            {...event}
                          />
                        </Container>
                      );
                    }

                    return (
                      <ConditionalDraggableCard
                        key={`${j}-${start}-${id}`}
                        planningEntityId={planningEntityId}
                        planningEntityType={planningEntityType}
                        scheduleStart=""
                        selected={selectedIds.includes(id)}
                        onChangeMousePosition={onChangeMousePosition}
                        onHandleSelect={onHandleSelect}
                        isToPlan={true}
                        {...event}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
      </List>
    );
  }
);

export default SortedToPlanList;
